import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { invoicesAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faSpinner, faFolderOpen, faFileCircleXmark } from "@fortawesome/free-solid-svg-icons";

import "../../../css/Views/InvoiceTransfersOverview.css";

const InvoiceTransfersOverview = ({ year, month, statusFilter, search }) => {
    const { callApi, isLoading } = useApi();

    const [transfers, setTransfers] = useState([]);
    const [expandedTransferId, setExpandedTransferId] = useState(null);
    const [editingNoInvoiceId, setEditingNoInvoiceId] = useState(null);
    const [noInvoiceComment, setNoInvoiceComment] = useState("");
    const [savingNoInvoiceId, setSavingNoInvoiceId] = useState(null);

    const [sortBy, setSortBy] = useState("transferDate");
    const [sortDirection, setSortDirection] = useState("desc");

    const getStatusApiValue = () => {
        switch (statusFilter) {
            case "unmatched":
                return 1;
            case "matched":
                return 2;
            case "multiple":
                return 3;
            case "noInvoice":
                return 4;
            default:
                return null;
        }
    };

    const loadTransfers = () => {
        callApi(() => invoicesAPI.getTransfersOverview({ year, month, status: getStatusApiValue() }), (data) => {
            setTransfers(Array.isArray(data) ? data : []);
        });
    };

    useEffect(() => {
        setExpandedTransferId(null);
        setEditingNoInvoiceId(null);
        loadTransfers();
    }, [year, month, statusFilter]);

    const getTransferStatus = (transfer) => {
        if (transfer.doesNotRequireInvoice)
            return "noInvoice";

        const count = transfer.documents?.length ?? 0;

        if (count === 0)
            return "unmatched";

        if (count === 1)
            return "matched";

        return "multiple";
    };

    const getStatusBadge = (transfer) => {
        const status = getTransferStatus(transfer);

        switch (status) {
            case "unmatched":
                return { text: "Bez dopasowania", className: "invoice-transfer-status-unmatched" };
            case "matched":
                return { text: "Powiązany", className: "invoice-transfer-status-matched" };
            case "multiple":
                return { text: "Wiele faktur", className: "invoice-transfer-status-multiple" };
            case "noInvoice":
                return { text: "Nie wymaga faktury", className: "invoice-transfer-status-no-invoice" };
            default:
                return { text: "—", className: "" };
        }
    };

    const formatDate = (value) => {
        if (!value)
            return "—";

        return new Date(value).toLocaleDateString("pl-PL");
    };

    const formatAmount = (amount, currency = "PLN") => {
        if (amount == null)
            return "—";

        return new Intl.NumberFormat("pl-PL", {
            style: "currency",
            currency: currency || "PLN"
        }).format(Math.abs(amount));
    };

    const openDriveFile = (googleDriveFileId) => {
        if (!googleDriveFileId)
            return;

        window.open(`https://drive.google.com/file/d/${googleDriveFileId}/view`, "_blank", "noopener,noreferrer");
    };

    const openDriveFolder = (sourceFolderId) => {
        if (!sourceFolderId)
            return;

        window.open(`https://drive.google.com/drive/folders/${sourceFolderId}`, "_blank", "noopener,noreferrer");
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection((current) => current === "asc" ? "desc" : "asc");
            return;
        }

        setSortBy(column);
        setSortDirection("asc");
    };

    const getSortIndicator = (column) => {
        if (sortBy !== column)
            return null;

        return sortDirection === "asc" ? " ↑" : " ↓";
    };

    const visibleTransfers = useMemo(() => {
        const searchValue = (search ?? "").trim().toLowerCase();

        return [...transfers]
            .filter((transfer) => {
                if (!searchValue)
                    return true;

                const amount = Math.abs(transfer.amount ?? 0).toFixed(2).replace(".", ",");

                return (
                    (transfer.contrahent ?? "").toLowerCase().includes(searchValue) ||
                    (transfer.title ?? "").toLowerCase().includes(searchValue) ||
                    amount.includes(searchValue)
                );
            })
            .sort((a, b) => {
                let valueA;
                let valueB;

                switch (sortBy) {
                    case "contractor":
                        valueA = a.contrahent ?? "";
                        valueB = b.contrahent ?? "";
                        break;

                    case "title":
                        valueA = a.title ?? "";
                        valueB = b.title ?? "";
                        break;

                    case "amount":
                        valueA = Math.abs(a.amount ?? 0);
                        valueB = Math.abs(b.amount ?? 0);
                        break;

                    case "documents":
                        valueA = a.documents?.length ?? 0;
                        valueB = b.documents?.length ?? 0;
                        break;

                    case "status":
                        valueA = getStatusBadge(a).text;
                        valueB = getStatusBadge(b).text;
                        break;

                    case "transferDate":
                    default:
                        valueA = a.transferDate ? new Date(a.transferDate).getTime() : 0;
                        valueB = b.transferDate ? new Date(b.transferDate).getTime() : 0;
                        break;
                }

                let result;

                if (typeof valueA === "string" && typeof valueB === "string")
                    result = valueA.localeCompare(valueB, "pl", { sensitivity: "base", numeric: true });
                else
                    result = valueA - valueB;

                return sortDirection === "asc" ? result : -result;
            });
    }, [transfers, search, sortBy, sortDirection]);

    const handleExpand = (transferId) => {
        setExpandedTransferId((current) => current === transferId ? null : transferId);
    };

    const startMarkNoInvoice = (transfer) => {
        setEditingNoInvoiceId(transfer.id);
        setNoInvoiceComment(transfer.invoiceExceptionComment ?? "");
    };

    const cancelMarkNoInvoice = () => {
        setEditingNoInvoiceId(null);
        setNoInvoiceComment("");
    };

    const saveNoInvoice = (transferId) => {
        if (!noInvoiceComment.trim() || savingNoInvoiceId != null)
            return;

        setSavingNoInvoiceId(transferId);

        callApi(() => invoicesAPI.setInvoiceRequirement(transferId, { doesNotRequireInvoice: true, comment: noInvoiceComment.trim() }), () => {
            setSavingNoInvoiceId(null);
            setEditingNoInvoiceId(null);
            setNoInvoiceComment("");
            loadTransfers();
        });
    };

    const restoreInvoiceRequirement = (transferId) => {
        if (savingNoInvoiceId != null)
            return;

        setSavingNoInvoiceId(transferId);

        callApi(() => invoicesAPI.setInvoiceRequirement(transferId, { doesNotRequireInvoice: false, comment: null }), () => {
            setSavingNoInvoiceId(null);
            loadTransfers();
        });
    };

    return (
        <div className="invoice-transfers-overview">
            {isLoading && <Loading text="Ładowanie przelewów..." />}

            {!isLoading && visibleTransfers.length === 0 && (
                <div className="empty-message">Brak przelewów spełniających wybrane kryteria.</div>
            )}

            {!isLoading && visibleTransfers.length > 0 && (
                <div className="invoice-transfers-overview-table-wrapper">
                    <table className="invoice-transfers-overview-table">
                        <thead>
                            <tr>
                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("transferDate")}>
                                    Data{getSortIndicator("transferDate")}
                                </th>

                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("contractor")}>
                                    Kontrahent{getSortIndicator("contractor")}
                                </th>

                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("title")}>
                                    Tytuł{getSortIndicator("title")}
                                </th>

                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("amount")}>
                                    Kwota{getSortIndicator("amount")}
                                </th>

                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("documents")}>
                                    Faktury{getSortIndicator("documents")}
                                </th>

                                <th className="invoice-transfer-sortable-header" onClick={() => handleSort("status")}>
                                    Status{getSortIndicator("status")}
                                </th>

                                <th>Akcja</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleTransfers.map((transfer) => {
                                const transferStatus = getTransferStatus(transfer);
                                const status = getStatusBadge(transfer);
                                const expanded = expandedTransferId === transfer.id;
                                const editing = editingNoInvoiceId === transfer.id;
                                const saving = savingNoInvoiceId === transfer.id;
                                const documents = transfer.documents ?? [];

                                return (
                                    <React.Fragment key={transfer.id}>
                                        <tr className={`invoice-transfer-overview-row invoice-transfer-row-${transferStatus}`}>
                                            <td className="invoice-transfer-overview-date">{formatDate(transfer.transferDate)}</td>

                                            <td className="invoice-transfer-overview-contractor">
                                                <span title={transfer.contrahent ?? ""}>{transfer.contrahent ?? "—"}</span>
                                            </td>

                                            <td className="invoice-transfer-overview-title">
                                                <span title={transfer.title ?? ""}>{transfer.title ?? "—"}</span>
                                            </td>

                                            <td className="invoice-transfer-overview-amount">{formatAmount(transfer.amount)}</td>

                                            <td>
                                                <div className={`invoice-transfer-documents-count ${documents.length > 0 ? "invoice-transfer-documents-count-clickable" : ""}`} onClick={() => {
                                                    if (documents.length > 0)
                                                        handleExpand(transfer.id);
                                                }}>
                                                    {documents.length === 0 ? "—" : documents.length === 1 ? "1 faktura" : `${documents.length} faktur`}
                                                    {documents.length > 0 && <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />}
                                                </div>
                                            </td>

                                            <td>
                                                <span className={`invoice-transfer-overview-status ${status.className}`}>{status.text}</span>
                                            </td>

                                            <td className="invoice-transfer-overview-actions-cell">
                                                {transfer.doesNotRequireInvoice ? (
                                                    <div className={`invoice-transfer-overview-action ${saving ? "invoice-transfer-overview-action-disabled" : ""}`} onClick={() => restoreInvoiceRequirement(transfer.id)}>
                                                        {saving ? (
                                                            <>
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                                Zapisywanie...
                                                            </>
                                                        ) : (
                                                            "Cofnij"
                                                        )}
                                                    </div>
                                                ) : documents.length === 0 ? (
                                                    <div className="invoice-transfer-overview-action invoice-transfer-overview-action-no-invoice" onClick={() => startMarkNoInvoice(transfer)}>
                                                        <FontAwesomeIcon icon={faFileCircleXmark} />
                                                        Bez faktury
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>

                                        {transfer.doesNotRequireInvoice && transfer.invoiceExceptionComment && (
                                            <tr className="invoice-transfer-no-invoice-comment-row">
                                                <td colSpan={7}>
                                                    <div className="invoice-transfer-no-invoice-comment">
                                                        <span>Powód:</span>
                                                        {transfer.invoiceExceptionComment}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {editing && (
                                            <tr className="invoice-transfer-no-invoice-edit-row">
                                                <td colSpan={7}>
                                                    <div className="invoice-transfer-no-invoice-edit">
                                                        <div className="invoice-transfer-no-invoice-edit-title">Dlaczego ten przelew nie wymaga faktury?</div>

                                                        <input type="text" value={noInvoiceComment} onChange={(e) => setNoInvoiceComment(e.target.value)} placeholder="Np. opłata urzędowa, prowizja bankowa..." autoFocus />

                                                        <div className="invoice-transfer-no-invoice-edit-actions">
                                                            <div className="invoice-transfer-overview-action" onClick={cancelMarkNoInvoice}>Anuluj</div>

                                                            <div className={`invoice-transfer-overview-action invoice-transfer-overview-action-primary ${!noInvoiceComment.trim() || saving ? "invoice-transfer-overview-action-disabled" : ""}`} onClick={() => saveNoInvoice(transfer.id)}>
                                                                {saving ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                                        Zapisywanie...
                                                                    </>
                                                                ) : (
                                                                    "Zapisz"
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {expanded && documents.length > 0 && (
                                            <tr className="invoice-transfer-documents-row">
                                                <td colSpan={7}>
                                                    <div className="invoice-transfer-documents-list">
                                                        {documents.map((document) => (
                                                            <div key={document.id} className="invoice-transfer-document">
                                                                <div className="invoice-transfer-document-main">
                                                                    <div className={`invoice-transfer-document-file ${document.googleDriveFileId ? "invoice-transfer-document-link" : ""}`} onClick={() => openDriveFile(document.googleDriveFileId)} title={document.googleDriveFileId ? "Otwórz plik na Dysku Google" : undefined}>
                                                                        {document.fileName}
                                                                    </div>

                                                                    <div className="invoice-transfer-document-details">
                                                                        {document.documentNumber && <span>{document.documentNumber}</span>}
                                                                        {document.contractor && <span>{document.contractor}</span>}

                                                                        {document.sourceFolderName && (
                                                                            <span className={`invoice-transfer-document-folder ${document.sourceFolderId ? "invoice-transfer-document-folder-clickable" : ""}`} onClick={() => openDriveFolder(document.sourceFolderId)} title={document.sourceFolderId ? "Otwórz folder na Dysku Google" : undefined}>
                                                                                <FontAwesomeIcon icon={faFolderOpen} />
                                                                                {document.sourceFolderName}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="invoice-transfer-document-amount">{formatAmount(document.grossAmount, document.currency)}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default InvoiceTransfersOverview;